import styled, {css} from 'styled-components';

import BaseCheckedIcon from '@edna/icons/checked.svg';

import {mixins} from '../styles';
import {TSize} from '../types';

type TCheckboxProps = TTheme & {
  disabled?: boolean;
  readOnly?: boolean;
  size?: TSize;
};

type TControlProps = TTheme & {
  checked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  isInvalid?: boolean;
  indeterminate?: boolean;
};

const getCheckboxDisabledStyle = ({disabled, readOnly, theme}: TCheckboxProps) => {
  if (disabled) {
    return css`
      cursor: not-allowed;
      color: ${theme.palette.grey300};
    `;
  }

  if (readOnly) {
    return css`
      cursor: default;
    `;
  }

  return css`
    cursor: pointer;
  `;
};

const getCheckboxFontSize = ({size}: TCheckboxProps) => {
  switch (size) {
    case 'S':
      return css`
        ${mixins.f2}
      `;
    case 'L':
      return css`
        ${mixins.f4}
        line-height: 20px;
      `;
    default:
    case 'M':
      return css`
        ${mixins.f3}
      `;
  }
};

export const Checkbox = styled.label<TCheckboxProps>`
  /* for HiddenInput */
  position: relative;
  display: flex;
  justify-content: unset;
  ${getCheckboxDisabledStyle}
  ${getCheckboxFontSize};
`;

export const ControlWrapper = styled.div<TCheckboxProps>`
  ${mixins.flexAlign('center')}
  flex-shrink: 0;
  ${({size, theme}) => css`
    ${mixins.wh(size === 'S' ? '16px' : '20px')}
    margin: ${theme.spacing.s05};
  `}
`;

const getControlStyle = ({
  theme: {palette, branding},
  checked,
  disabled,
  readOnly,
  isInvalid,
  indeterminate,
}: TControlProps) => {
  if (disabled) {
    return css`
      background-color: ${checked ? palette.grey300 : 'transparent'};
      border-color: ${palette.grey300};
    `;
  }

  if (readOnly) {
    return css`
      background-color: ${checked ? branding.content.tertiary : 'transparent'};
      border-color: ${branding.content.tertiary};
    `;
  }

  if (isInvalid) {
    return css`
      border-color: ${palette.red400};
    `;
  }

  return css`
    background-color: ${checked ? branding.content.primary : 'transparent'};
    border-color: ${branding.content.primary};
    ${!checked &&
    indeterminate &&
    css`
      position: relative;
      &::after {
        position: absolute;
        top: 50%;
        transform: translate(0, -50%);
        content: '';
        width: calc(50% + 2px);
        height: calc(50% + 2px);
        background-color: ${branding.content.primary};
      }
    `}
  `;
};

export const Control = styled.span<TControlProps>`
  ${mixins.flexAlign('center')}
  ${mixins.wh('100%')}
  ${({theme: {palette, branding, borderRadius}}) => css`
    color: ${palette.white};
    border: 2px solid ${branding.content.primary};
    border-radius: ${borderRadius.checkbox};
  `}
  ${getControlStyle}
`;

export const Label = styled.span`
  ${mixins.breakWord}
  ${({theme}) => css`
    margin-left: ${theme.spacing.s2};
    padding: ${theme.spacing.s05} 0;
  `}
`;

export const CheckedIcon = styled(BaseCheckedIcon)`
  ${mixins.wh('100%')}
`;
